import { get, intersection, isArray, isNull, isUndefined } from 'lodash';

import { permissions } from '../constants/permissions';
import { getShowDictionaryForSAFlag } from './AuthenticationRules';

export const chargerStatusSelectAccessId = 'chargerStatusSelect';
export const adminToolsTabAccessId = 'adminTools';
export const firmwareUpdateBtnAccessId = 'firmwareUpdateBtn';
export const viewFirmwareUpdatePageAccessId = 'viewFirmwareUpdatePage';
export const editChargerDetailsAccessId = 'editChargerDetails';
export const adminToolsMenuAccessId = 'adminToolsMenu';
export const customerAdminToolsMenuAccessId = 'customerAdminTools';
export const ssoAccountAdminToolsMenuAccessId = 'ssoAccountAdminTools';
export const fieldMappingMenuAccessId = 'fieldMapping';
export const firmwareVersionsMenuAccessId = 'FWPackages';
export const failuresMenuAccessId = 'failures';
export const accountsAccessId = 'Accounts';
export const acHwVersionAccessId = 'Ac-hw-version';
export const countriesAccessId = 'Countries';
export const chademoAccessId = 'Chademo';
export const enclosuresAccessId = 'Enclosures';
export const maintainersAccessId = 'Maintainers';
export const ownersAccessId = 'Owners';
export const productLevelAccessId = 'Product-level';
export const productTypesAccessId = 'Product-types';
export const productVersionsAccessId = 'Product-versions';
export const supervisorsAccessId = 'Supervisors';
export const accountFormRolesAccessId = 'AccountFormRoles';
export const rolesAccessId = 'roles';
export const addRoleAccessId = 'addRole';
export const reportsAccessId = 'reports';
export const resetGIRAccessId = 'resetGIR';
export const resetNotificationsAccessId = 'resetNotifications';
export const resetBoardAccessId = 'resetBoard';
export const resetPWB1AccessId = 'resetPWB1';
export const importDictionaryAccessId = 'importDictionary';
export const parametersSchemaMenuAccessId = 'parametersSchema';
export const viewChargerTaskAccessId = 'viewChargerTask';
export const createChargerTaskAccessId = 'createChargerTask';
export const controlChargerTaskAccessId = 'controlChargerTask';
export const profilePageAccessId = 'profilePage';
export const telemetryMetricsMenuAccessId = 'telemetryMetrics';
export const viewChargerTelemetry = 'viewChargerTelemetry';
export const editPlaza = 'editPlaza';
export const viewChargerConfigsId = 'viewChargerConfigs';
export const viewChargerCustomer = 'viewChargerCustomer';
export const editChargerConfigsId = 'editChargerConfigs';
export const AWSSSO = 'AWSSSO';
export const edgePageAccessId = 'edgePage';

const checkRole = (requiredPermissions, userPermissions, excludedPermissions = []) =>
  intersection(requiredPermissions, userPermissions).length > 0 && intersection(requiredPermissions, excludedPermissions).length === 0;

export const checkLoggedFromSupervisor = (user) => {
  if (!user || !user.currentPermissions || !user.initialRoles) return false;

  const hasNoSupervisorPermission = !user.currentPermissions.includes(permissions.SUPERVISOR);
  const hasSupervisorInitialRole = !!user.initialRoles.find((role) => get(role, 'roleName', '').toUpperCase() === permissions.SUPERVISOR);

  return hasNoSupervisorPermission && hasSupervisorInitialRole;
};

const checkShowProfile = (user, config) => {
  let showProfile = true;
  const userProviderType = get(user, 'authProvider.type');

  if (userProviderType === AWSSSO) return false;

  const customerAuthProviders = get(config, 'authProviders');

  if (!isNull(customerAuthProviders) && isArray(customerAuthProviders) && !isNull(userProviderType)) {
    const provider = customerAuthProviders.find((provider) => provider.type === userProviderType);

    showProfile = provider && !isUndefined(provider.showProfile) ? provider.showProfile : false;

    if (checkLoggedFromSupervisor(user)) {
      showProfile = false;
    }
  }

  return showProfile;
};

const hasAccess = (elementId, userPermissions, config = {}) => {
  const showDictionaries = getShowDictionaryForSAFlag(config);
  const excludedPermissions = get(config, 'excluded_permissions') || [];

  switch (elementId) {
    case parametersSchemaMenuAccessId:
    case fieldMappingMenuAccessId:
    case firmwareVersionsMenuAccessId:
    case failuresMenuAccessId:
      return checkRole([permissions.FULL_ACCESS], userPermissions) && !showDictionaries;
    case customerAdminToolsMenuAccessId: {
      const isSupervisor = checkRole([permissions.SUPERVISOR], userPermissions);

      if (isSupervisor) return isSupervisor;

      return checkRole([permissions.FULL_ACCESS], userPermissions) && showDictionaries;
    }
    case ssoAccountAdminToolsMenuAccessId:
      return checkRole([permissions.FULL_ACCESS], userPermissions) && showDictionaries;
    case addRoleAccessId:
    case importDictionaryAccessId:
      return checkRole([permissions.FULL_ACCESS], userPermissions);
    case rolesAccessId:
    case accountsAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.EDIT_USER_RELATED_DICTIONARIES], userPermissions) && !showDictionaries;
    case adminToolsTabAccessId:
    case adminToolsMenuAccessId: {
      const requiredPermissions = [
        permissions.FULL_ACCESS,
        permissions.EDIT_COMMON_DICTIONARIES,
        permissions.EDIT_USER_RELATED_DICTIONARIES,
      ];

      return checkRole(requiredPermissions, userPermissions);
    }
    case countriesAccessId:
    case enclosuresAccessId:
    case maintainersAccessId:
    case chademoAccessId:
    case ownersAccessId:
    case productLevelAccessId:
    case productTypesAccessId:
    case productVersionsAccessId:
    case supervisorsAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.EDIT_COMMON_DICTIONARIES], userPermissions) && !showDictionaries;
    case accountFormRolesAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.EDIT_USER_RELATED_DICTIONARIES], userPermissions) && !showDictionaries;
    case reportsAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.VIEW_REPORTS], userPermissions);
    case editChargerDetailsAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.EDIT_CHARGER_DETAILS], userPermissions);
    case chargerStatusSelectAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.EDIT_CHARGER_AVAILABILITY], userPermissions);
    case firmwareUpdateBtnAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.UPDATE_CHARGER_FW], userPermissions);
    case viewFirmwareUpdatePageAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.VIEW_FW_UPDATE_PAGE], userPermissions);
    case resetBoardAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.RESET_I2P2_BOARD, permissions.SUPERVISOR], userPermissions);
    case resetGIRAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.RESET_GIR, permissions.SUPERVISOR], userPermissions);
    case resetNotificationsAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.RESET_NOTIFICATION, permissions.SUPERVISOR], userPermissions);
    case resetPWB1AccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.RESET_PWB1, permissions.SUPERVISOR], userPermissions);
    case profilePageAccessId:
      return config ? checkShowProfile(userPermissions, config) : false;
    case viewChargerTaskAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.VIEW_CHARGER_TASK], userPermissions);
    case createChargerTaskAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.CREATE_CHARGER_TASK], userPermissions);
    case controlChargerTaskAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.CONTROL_CHARGER_TASK], userPermissions);
    case telemetryMetricsMenuAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.TELEMETRY_METRICS], userPermissions) && !showDictionaries;
    case viewChargerTelemetry:
      return checkRole([permissions.FULL_ACCESS, permissions.VIEW_CHARGER_TELEMETRY], userPermissions, excludedPermissions);
    case editPlaza:
      return checkRole([permissions.FULL_ACCESS, permissions.EDIT_PLAZA], userPermissions);
    case viewChargerConfigsId:
      return checkRole([permissions.FULL_ACCESS, permissions.VIEW_CHARGER_CONFIGURATION], userPermissions);
    case viewChargerCustomer:
      return checkRole([permissions.SUPERVISOR], userPermissions);
    case editChargerConfigsId:
      return checkRole([permissions.FULL_ACCESS, permissions.EDIT_CHARGER_CONFIGURATION], userPermissions);
    case edgePageAccessId:
      return checkRole([permissions.FULL_ACCESS, permissions.SUPERVISOR], userPermissions);
    default:
      return false;
  }
};

export const isDirectlySupervisor = (user) => {
  if (!user || !user.currentPermissions) return false;

  return user.currentPermissions.includes(permissions.SUPERVISOR);
};

export const isSupervisor = (user) => {
  if (!user) return false;

  const hasSupervisorInitialRole = !!user.initialRoles.find((role) => get(role, 'roleName', '').toUpperCase() === permissions.SUPERVISOR);

  return isDirectlySupervisor(user) || hasSupervisorInitialRole;
};

export default hasAccess;
